import { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { Box, Flex, Image, Link, useBreakpointValue } from '@chakra-ui/react';
import parse from 'html-react-parser';
import { FormattedMessage } from 'react-intl';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Mousewheel, Navigation, Pagination, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { SwiperRef } from 'swiper/react';
import { Card, Header, Text } from '../../ui-components';
import { CarouselWrapper } from '../../ui-components/carousel/CarouselWrapper';

interface Category {
    name: string;
    url: string;
    image: string;
}

const categories: Category[] = [
    {
        image: 'https://assets.surlatable.com/m/54d330e29445baed/72_dpi_webp-7892474_0623_01i_e.webp',
        url: '/products/cookware/',
        name: 'Cookware',
    },
    {
        image: 'https://assets.surlatable.com/m/3ea65f0c2beb050d/72_dpi_webp-7908189_0523_04i_e.webp',
        url: '/products/coffee-tea/coffee-espresso/',
        name: 'Coffee & Espresso',
    },
    {
        image: 'https://assets.surlatable.com/m/6d2e173e65ff50ff/72_dpi_webp-SLT-Mixed-Tool-Collection.webp',
        url: '/products/kitchen-tools/',
        name: 'Kitchen Tools',
    },
    {
        image: 'https://assets.surlatable.com/m/1de67c9fada82d5/72_dpi_webp-8056228_0623_01i_eSQ.webp',
        url: '/products/knives/',
        name: 'Knives',
    },
    {
        image: 'https://assets.surlatable.com/m/37dbf87c461a8f83/72_dpi_webp-7275233_0623_01i_eSQ.webp',
        url: '/products/dining-bar/',
        name: 'Dining',
    },
    {
        image: 'https://assets.surlatable.com/m/625a22d03bc78bfd/72_dpi_webp-7776453_0623_01i_eSQ.webp',
        url: '/products/bakeware/',
        name: 'Bakeware',
    },
    {
        image: 'https://assets.surlatable.com/m/d383883f194240a/72_dpi_webp-3179272_0123_03i_eSQ.webp',
        url: '/products/small-appliances/',
        name: 'Small Appliances',
    },
    {
        image: 'https://assets.surlatable.com/m/fbc46b4cc576c41/72_dpi_webp-7691587_0123_01i_eSQ.webp',
        url: '/products/small-appliances/brands/breville/',
        name: 'Breville',
    },
    {
        image: 'https://assets.surlatable.com/m/63afd977a1ad151b/72_dpi_webp-8058877_0623_01i_eSQ.webp',
        url: '/products/cookware/brands/le-creuset/',
        name: 'Le Creuset',
    },
    {
        image: 'https://rstatic.locations.surlatable.com.prod.rioseo.com/domain/7230_2949_New_Jardin_Dinnerware_Stores_SQ.jpg',
        url: '/new-1/',
        name: 'New',
    },
];

export const EnhancedErrorCategories: FC = () => {
    const shouldShowNavigation = useBreakpointValue({ base: false, lg: true });
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const swiperRef = useRef<SwiperRef | null>(null);

    useEffect(() => {
        const handleMouseEnter = (): void => {
            setIsHovered(true);
        };

        const handleMouseLeave = (): void => {
            setIsHovered(false);
        };

        const swiper = document.getElementById('enhanced-error-categories-swiper');

        swiper?.addEventListener('mouseenter', handleMouseEnter);
        swiper?.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            swiper?.removeEventListener('mouseenter', handleMouseEnter);
            swiper?.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, []);
    return (
        <div className="mb-4 gap-4 p-4 md:px-8 lg:px-10 2xl:px-16">
            <Header as="h4" style="bold" variant="header4">
                Shop Featured Categories
            </Header>

            <CarouselWrapper>
                <Swiper
                    id="enhanced-error-categories-swiper"
                    ref={swiperRef}
                    slidesPerView="auto"
                    spaceBetween={16}
                    pagination={{
                        clickable: true,
                    }}
                    rewind
                    modules={[Pagination, Scrollbar, Navigation, Mousewheel]}
                    lazyPreloadPrevNext={6}
                    navigation={shouldShowNavigation && isHovered}
                    scrollbar
                    mousewheel={{ forceToAxis: true }}
                >
                    {categories.map((item, index) => (
                        <SwiperSlide key={item.url} virtualIndex={index}>
                            <Card
                                maxW="375px"
                                padding="0px"
                                key={item.url}
                                borderRadius="16px 16px 0px 0px"
                            >
                                <Box display="flex" flexDirection="column" p="0">
                                    <Image
                                        src={item.image}
                                        marginTop="1px"
                                        alt={item.name}
                                        minWidth="100%"
                                        aspectRatio={1}
                                        borderRadius="1rem"
                                    />
                                    <Flex
                                        direction="column"
                                        alignItems="start"
                                        p="1rem"
                                        gap="0.5rem"
                                    >
                                        <Box display="flex" flexGrow="1" h="48px">
                                            <Text noOfLines={2} type="body" style="bold">
                                                {parse(item.name)}
                                            </Text>
                                        </Box>
                                        <Link href={item.url} mt={1} textDecoration="underline">
                                            <Text noOfLines={2} type="body">
                                                <FormattedMessage
                                                    id="error.generic.category.link"
                                                    defaultMessage="Show More"
                                                />
                                            </Text>
                                        </Link>
                                    </Flex>
                                </Box>
                            </Card>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </CarouselWrapper>
        </div>
    );
};
